// components/PageSEO.jsx
import React from 'react'
import { Helmet } from 'react-helmet-async'

const DEFAULTS = {
  siteName:    'WebNX',
  twitter:     '@WebNX',
  ogImage:     'https://webnx.com/images/webnx-og-image.jpg',
  orgLogo:     'https://webnx.com/images/logo.png',
  lang:        'en',
  robots:      'index,follow',
  cardType:    'summary_large_image',
  mobileViewport: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
  themeColor:  '#000000',
  appleTouchIcon: 'https://webnx.com/images/apple-touch-icon.png',
  favicon:     'https://webnx.com/favicon.ico'
}

const PageSEO = ({
  title,                // [string] page title (without "| WebNX")
  description,          // [string] meta description
  keywords,             // [string] comma–separated keywords
  image,                // [string] full URL to OG/Twitter image
  path,                 // [string] path after domain (e.g. "/blog/my-slug")
  author,               // [string] author name
  publishDate,          // [string] ISO date of publication
  modifiedDate,         // [string] ISO date of last modification
  article,              // [bool] true → og:type="article" + Article JSON-LD
  noIndex,              // [bool] true → <meta name="robots" content="noindex,…" />
  noFollow,             // [bool] true → <meta name="robots" content="…,nofollow" />
  lang,                 // [string] html lang attribute
  schema,               // [object] any additional JSON-LD to inject
  alternateLanguages,   // [array] array of {lang: string, url: string} objects
  socialProfiles,       // [object] {facebook, twitter, instagram, linkedin} profile URLs
  articleTags,          // [array] array of article tags
  articleSection,       // [string] article section/category
  mobileViewport,       // [string] custom viewport meta tag
  themeColor,           // [string] theme color for mobile browsers
  appleTouchIcon,       // [string] custom apple touch icon URL
  favicon              // [string] custom favicon URL
}) => {
  const url       = path ? `https://webnx.com${path}` : undefined
  const fullTitle = title ? `${title} | ${DEFAULTS.siteName}` : DEFAULTS.siteName
  const robots    = noIndex || noFollow
    ? `${noIndex ? 'noindex' : 'index'},${noFollow ? 'nofollow' : 'follow'}`
    : DEFAULTS.robots

  // Base Website JSON-LD
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type':    'WebSite',
    ...(url && { url }),
    name:       fullTitle,
    publisher: {
      '@type': 'Organization',
      name:    DEFAULTS.siteName,
      logo: {
        '@type': 'ImageObject',
        url:     DEFAULTS.orgLogo
      },
      ...(socialProfiles && {
        sameAs: Object.values(socialProfiles).filter(Boolean)
      })
    }
  }

  // Article JSON-LD (if article=true and publishDate is provided)
  const articleSchema = article && publishDate && {
    '@context':        'https://schema.org',
    '@type':           'Article',
    mainEntityOfPage: url && { '@type': 'WebPage', '@id': url },
    headline:          title,
    ...(image && { image: [image] }),
    datePublished:     publishDate,
    dateModified:      modifiedDate || publishDate,
    author: {
      '@type': 'Person',
      name:    author || DEFAULTS.siteName
    },
    publisher: {
      '@type': 'Organization',
      name:    DEFAULTS.siteName,
      logo: {
        '@type': 'ImageObject',
        url:     DEFAULTS.orgLogo
      }
    },
    ...(description && { description }),
    ...(articleTags && { keywords: articleTags.join(', ') }),
    ...(articleSection && { articleSection })
  }

  return (
    <Helmet>
      {/* HTML + Language */}
      {lang && <html lang={lang} />}

      {/* Title & Basic */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords    && <meta name="keywords" content={keywords} />}
      {author      && <meta name="author" content={author} />}
      {robots      && <meta name="robots" content={robots} />}

      {/* Open Graph */}
      <meta property="og:type"      content={article ? 'article' : 'website'} />
      <meta property="og:site_name" content={DEFAULTS.siteName} />
      <meta property="og:title"     content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {url         && <meta property="og:url"         content={url} />}
      <meta property="og:image"     content={image || DEFAULTS.ogImage} />
      {article && articleTags && <meta property="article:tag" content={articleTags.join(', ')} />}
      {article && articleSection && <meta property="article:section" content={articleSection} />}
      {article && publishDate && <meta property="article:published_time" content={publishDate} />}
      {article && modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}

      {/* Twitter Card */}
      <meta name="twitter:card"        content={DEFAULTS.cardType} />
      <meta name="twitter:site"        content={DEFAULTS.twitter} />
      <meta name="twitter:creator"     content={DEFAULTS.twitter} />
      <meta name="twitter:title"       content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image"       content={image || DEFAULTS.ogImage} />

      {/* Canonical */}
      {url && <link rel="canonical" href={url} />}

      {/* Alternate Languages */}
      {alternateLanguages?.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}

      {/* Mobile & Theme */}
      <meta name="viewport" content={mobileViewport || DEFAULTS.mobileViewport} />
      <meta name="theme-color" content={themeColor || DEFAULTS.themeColor} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <link rel="apple-touch-icon" href={appleTouchIcon || DEFAULTS.appleTouchIcon} />
      <link rel="icon" href={favicon || DEFAULTS.favicon} />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  )
}

export default PageSEO
